import analytics from './analytics';
import bankAccount from './bankAccount';
import caseFinancialSummary from './caseFinancialSummary';
import campaign from './campaign';
import comment from './comment';
import common from './common';
import config from './config';
import conflict from './conflict';
import contact from './contact';
import contactMethod from './contactMethod';
import contingency from './contingency';
import court from './court';
import deadlineRule from './deadlineRule';
import document from './document';
import entityMeta from './entityMeta';
import event from './event';
import filterDialog from './filterDialog';
import flatFee from './flatFee';
import home from './home';
import id from './id';
import importExport from './importExport';
import intake from './intake';
import invoice from './invoice';
import invoiceBatch from './invoiceBatch';
import invoicing from './invoicing';
import lead from './lead';
import matter from './case';
import payment from './payment';
import redFlag from './redFlag';
import relationship from './relationship';
import task from './task';
import transaction from './transaction';
import user from './user';
import wip from './wip';
import Alert from '../lib/alert';
import {captureException} from "../lib/picos/error";
import upFirstLetter from "../lib/picos/upFirstLetter";
import {ReactElement} from "react";

export default {
	/**
	 * @param {string} module
	 * @returns {any} - the module with strings
	 */
	getModule(module: string): any {
		try {
			switch (module) {
				case 'analytics':
					return analytics;
				case 'bankAccount':
					return bankAccount;
				case 'campaign':
					return campaign;
				case 'task':
					return task;
				case 'case':
					return matter;
				case 'caseFinancialSummary':
					return caseFinancialSummary;
				case 'comment':
					return comment;
				case 'common':
					return common;
				case 'config':
					return config;
				case 'conflict':
					return conflict;
				case 'contact':
					return contact;
				case 'contactMethod':
					return contactMethod;
				case 'contingency':
					return contingency;
				case 'court':
					return court;
				case 'deadlineRule':
					return deadlineRule;
				case 'document':
					return document;
				case 'entityMeta':
					return entityMeta;
				case 'event':
					return event;
				case 'filterDialog':
					return filterDialog;
				case 'flatFee':
					return flatFee;
				case 'home':
					return home;
				case 'id':
					return id;
				case 'importExport':
					return importExport;
				case 'intake':
					return intake;
				case 'invoice':
					return invoice;
				case 'invoiceBatch':
					return invoiceBatch;
				case 'invoicing':
					return invoicing;
				case 'lead':
					return lead;
				case 'payment':
					return payment;
				case 'redFlag':
					return redFlag;
				case 'relationship':
					return relationship;
				case 'transaction':
					return transaction;
				case 'user':
					return user;
				case 'wip':
					return wip;
				default: {
					console.error(`No such module: ${module}`);
					Alert.addSimpleError(`No such module: ${module}`);
					return {};
				}
			}
		}
		catch (err: any) {
			console.error(err);
			Alert.addSimpleError(err.message);
			return {};
		}
	},

	/**
	 * Get the label for the help message.
	 * If the label is a function, we call it passing in params.  This is important because
	 * languages do not always translate sentences word-for-word.
	 *
	 * @param {string} module
	 * @param {string} key
	 * @param {any} [params]
	 * @param {boolean} [fromCommon] - was this called from the c() function? If so, do NOT check c()
	 * @returns {ReactElement|string}
	 */
	l(module: string, key: string, params?: any, fromCommon?: boolean): ReactElement | string {
		try {
			const _module = this.getModule(module);
			const keyVal = _module[`d_${key}`];

			if (!keyVal) {
				if (!fromCommon) {
					// Try the common module
					const common = this.c(key, true, true);
					if (!common) {
						console.error(`No such key: ${module}/${key}`);
						return `${module}/${key}`;
					}
					else return common;
				}
			}
			else {
				if (typeof (keyVal) === 'function') return keyVal(params);
				else return keyVal;
			}
		}
		catch (err) {
			console.error(err);
			Alert.addSimpleError(`Error getting label for ${module}/${key}`);
		}
	},

	ls(module: string, key: string, params?: any, fromCommon?: boolean): string {
		const result: string = this.l(module, key, params, fromCommon) as string;
		if (typeof(result) === 'string') return result;
		else {
			console.warn(`i18.debug(${module}, ${key}) did not return a string`)
			return result;
		}
	},

	/**
	 * Get the description from the module, key and params.
	 *
	 * @param {string} module
	 * @param {string} key
	 * @param {any} [params]
	 * @returns {ReactElement|string}
	 */
	d(module: string, key: string, params?: any): ReactElement | string {
		try {
			const _module = this.getModule(module);
			const template = _module[`h_${key}`];

			if (!template) return `No such template: ${module}/${key}`;

			if (typeof (template) === 'function') return template(params);
			else return template;
		}
		catch (err) {
			console.error(err);
			Alert.addSimpleError(`Error getting description for ${module}/${key}`);
		}
	},

	/**
     * Get the description from the module, key and params.
     *
     * @param {string} module
     * @param {string} key
	 * @param {any} [params]
	 * @returns {string}
	 */
	ds(module: string, key: string, params?: any): string {
		return this.d(module, key, params) as string;
	},

	/**
	 * Quick access function for common functions.
	 *
	 * @param {string} key
	 * @param {boolean} [capitalized]
	 * @param {boolean} [fromLabel] - was this called from l()?
	 * @returns {JSX.Element|string}
	 */
	c(key: string, capitalized?: boolean, fromLabel?: boolean): ReactElement | string {
		try {
			const label = this.l('common', key, {}, true);
			if (!label) {
				// If this was called from the l() function, just return null
				if (fromLabel) return null;

				console.error(`common/${key} not found.`);
				return `c/${key} not found`;
			}
			if (capitalized) return upFirstLetter(label);
			else return label;
		}
		catch (err) {
			if (import.meta.env.MODE !== 'development') {
				captureException(err, {tags: ['i18n', 'c'], extra: []});
			}
			console.warn(err);
			return `Error in i18n.c('${key}', ${capitalized})`;
		}
	},

	cs(key: string, capitalized?: boolean, fromLabel?: boolean): string {
		return this.c(key, capitalized, fromLabel) as string;
	},

	/**
	 * Quick access function for common functions that require params.
	 *
	 * @param {string} key
	 * @param {any} params
	 * @param {boolean} [fromLabel] - was this called from l()?
	 * @returns {ReactElement|string}
	 */
	cv(key: string, params: any, fromLabel?: boolean): ReactElement | string {
		try {
			const label = this.l('common', key, params, true);
			if (!label) {
				// If this was called from the l() function, just return null
				if (fromLabel) return null;

				console.error(`common/${key} not found`);
				return `c/${key} not found`;
			}
			else return label;
		}
		catch (err) {
			console.error(err);
			return `Error in i18n.cv('${key}')`;
		}
	},

	cvs(key: string, params: any, fromLabel?: boolean): string {
        return this.cv(key, params, fromLabel) as string;
    }
}
